import { Group, PerspectiveCamera, Scene } from "three";
import {
	CSS3DObject,
	CSS3DRenderer,
} from "three/examples/jsm/renderers/CSS3DRenderer.js";

export function renderDepthFloat(items, renderItem) {
	const scene = document.createElement("div");
	scene.className = "sui-topic-cloud-scene sui-topic-cloud-scene--depth";

	items.forEach((item, index) => {
		scene.appendChild(renderItem(item, index, items.length, `${index}`));
	});

	return scene;
}

function seededUnit(seed, offset = 1) {
	const value = Math.sin(seed * (offset + 1) * 12.9898) * 43758.5453;
	return value - Math.floor(value);
}

function packItems(entries, bounds, spacing, clampValue) {
	const sortedEntries = [...entries].sort(
		(left, right) =>
			right.metric.width * right.metric.height -
			left.metric.width * left.metric.height,
	);
	const usableWidth = Math.max(bounds.width, 1);
	const usableHeight = Math.max(bounds.height, 1);

	sortedEntries.forEach((entry) => {
		const scaledWidth = entry.metric.width * entry.baseScale;
		const scaledHeight = entry.metric.height * entry.baseScale;
		const xRange = Math.max(0, usableWidth - scaledWidth);
		const yRange = Math.max(0, usableHeight - scaledHeight);

		entry.baseX = (seededUnit(entry.seed, 1) - 0.5) * xRange * 0.94;
		entry.baseY = (seededUnit(entry.seed, 2) - 0.5) * yRange * 0.9;
		entry.baseZ = (seededUnit(entry.seed, 3) * 2 - 1) * 180;
	});

	for (let passIndex = 0; passIndex < 36; passIndex += 1) {
		for (let leftIndex = 0; leftIndex < sortedEntries.length; leftIndex += 1) {
			const left = sortedEntries[leftIndex];
			const leftWidth = left.metric.width * left.baseScale;
			const leftHeight = left.metric.height * left.baseScale;

			for (
				let rightIndex = leftIndex + 1;
				rightIndex < sortedEntries.length;
				rightIndex += 1
			) {
				const right = sortedEntries[rightIndex];
				const rightWidth = right.metric.width * right.baseScale;
				const rightHeight = right.metric.height * right.baseScale;
				const deltaX = right.baseX - left.baseX;
				const deltaY = right.baseY - left.baseY;
				const minX = (leftWidth + rightWidth) / 2 + spacing.x;
				const minY = (leftHeight + rightHeight) / 2 + spacing.y;

				if (Math.abs(deltaX) >= minX || Math.abs(deltaY) >= minY) {
					continue;
				}

				const overlapX = minX - Math.abs(deltaX);
				const overlapY = minY - Math.abs(deltaY);

				if (overlapX < overlapY) {
					const direction =
						deltaX === 0
							? seededUnit(left.seed + right.seed, 11) > 0.5
								? 1
								: -1
							: Math.sign(deltaX);
					const shift = overlapX * 0.5;
					left.baseX -= direction * shift;
					right.baseX += direction * shift;
				} else {
					const direction =
						deltaY === 0
							? seededUnit(left.seed + right.seed, 13) > 0.5
								? 1
								: -1
							: Math.sign(deltaY);
					const shift = overlapY * 0.5;
					left.baseY -= direction * shift;
					right.baseY += direction * shift;
				}
			}
		}

		sortedEntries.forEach((entry) => {
			const scaledWidth = entry.metric.width * entry.baseScale;
			const scaledHeight = entry.metric.height * entry.baseScale;
			const xLimit = Math.max(0, usableWidth / 2 - scaledWidth / 2);
			const yLimit = Math.max(0, usableHeight / 2 - scaledHeight / 2);

			entry.baseX = clampValue(entry.baseX * 0.985, -xLimit, xLimit);
			entry.baseY = clampValue(entry.baseY * 0.985, -yLimit, yLimit);
		});
	}

	return sortedEntries;
}

export function initializeDepthFloat(node, { clamp }) {
	const sceneNode = node.querySelector(".sui-topic-cloud-scene--depth");
	const items = Array.from(node.querySelectorAll(".sui-topic-cloud-item"));
	const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

	if (!sceneNode || !items.length || motionQuery.matches) {
		return;
	}

	node.classList.add("is-three-active");

	const renderer = new CSS3DRenderer();
	renderer.domElement.className = "sui-topic-cloud-depth-renderer";

	const scene = new Scene();
	const camera = new PerspectiveCamera(38, 1, 1, 5000);
	const floatGroup = new Group();
	scene.add(floatGroup);

	const strength = Number(node.dataset.pointerStrength || 0.85);
	const itemMetrics = items.map((item) => {
		const rect = item.getBoundingClientRect();

		return {
			height: Math.max(rect.height || 0, 48),
			width: Math.max(rect.width || 0, 120),
		};
	});

	const floatObjects = items.map((item, index) => {
		const object = new CSS3DObject(item);
		const seed = Number(item.dataset.seed || index + 1);
		const baseScale = clamp(Number(item.dataset.scale || 1), 0.84, 1.35);

		return {
			baseScale,
			item,
			metric: itemMetrics[index],
			object,
			seed,
			targetScale: baseScale,
			targetX: 0,
			targetY: 0,
			targetZ: 0,
		};
	});

	floatObjects.forEach(({ object }) => {
		floatGroup.add(object);
	});

	sceneNode.replaceChildren(renderer.domElement);

	const pointer = { x: 0, y: 0 };
	const pointerTarget = { x: 0, y: 0 };
	let frameId = 0;
	let layout = {
		height: 320,
		horizontalHalfExtent: 160,
		verticalHalfExtent: 120,
		width: 320,
	};

	const renderScene = () => {
		renderer.render(scene, camera);
	};

	const updateLayout = () => {
		const width = sceneNode.clientWidth || 1;
		const isNarrow = width < 768;
		const horizontalPadding = isNarrow ? 24 : 42;
		const verticalPadding = isNarrow ? 56 : 78;
		const totalArea = floatObjects.reduce(
			(sum, entry) =>
				sum +
				entry.metric.width *
					entry.metric.height *
					entry.baseScale *
					entry.baseScale,
			0,
		);
		const contentWidth = Math.max(width - horizontalPadding * 2, 240);
		const targetDensity = isNarrow ? 0.44 : 0.52;
		const estimatedContentHeight =
			totalArea / Math.max(contentWidth * targetDensity, 1);
		const sceneHeight = clamp(
			estimatedContentHeight + verticalPadding * 2,
			340,
			isNarrow ? 720 : 820,
		);
		const packedEntries = packItems(
			floatObjects,
			{
				height: Math.max(sceneHeight - verticalPadding * 2, 240),
				width: contentWidth,
			},
			{
				x: isNarrow ? 10 : 14,
				y: isNarrow ? 8 : 12,
			},
			clamp,
		);

		let minX = 0;
		let maxX = 0;
		let minY = 0;
		let maxY = 0;

		packedEntries.forEach((entry) => {
			const halfWidth = (entry.metric.width * entry.baseScale) / 2;
			const halfHeight = (entry.metric.height * entry.baseScale) / 2;

			entry.targetX = entry.baseX;
			entry.targetY = entry.baseY;
			entry.targetZ = entry.baseZ;
			entry.object.position.set(entry.baseX, entry.baseY, entry.baseZ);
			entry.object.scale.set(entry.baseScale, entry.baseScale, entry.baseScale);

			minX = Math.min(minX, entry.baseX - halfWidth);
			maxX = Math.max(maxX, entry.baseX + halfWidth);
			minY = Math.min(minY, entry.baseY - halfHeight);
			maxY = Math.max(maxY, entry.baseY + halfHeight);
		});

		layout = {
			height: sceneHeight,
			horizontalHalfExtent: Math.max(Math.abs(minX), Math.abs(maxX)),
			verticalHalfExtent: Math.max(Math.abs(minY), Math.abs(maxY)),
			width,
		};

		sceneNode.style.minHeight = `${sceneHeight}px`;
		sceneNode.style.height = `${sceneHeight}px`;
		renderer.setSize(width, sceneHeight);
		camera.aspect = width / sceneHeight;
		const verticalFovRadians = (camera.fov * Math.PI) / 180;
		const horizontalFovRadians =
			2 * Math.atan(Math.tan(verticalFovRadians / 2) * camera.aspect);
		const distanceForHeight =
			(layout.verticalHalfExtent + verticalPadding) /
			Math.tan(verticalFovRadians / 2);
		const distanceForWidth =
			(layout.horizontalHalfExtent + horizontalPadding) /
			Math.tan(horizontalFovRadians / 2);

		camera.position.set(
			0,
			0,
			Math.max(distanceForHeight, distanceForWidth, 760),
		);
		camera.lookAt(0, 0, 0);
		camera.updateProjectionMatrix();
		renderScene();
	};

	const updateTargets = () => {
		const focusX = pointer.x;
		const focusY = pointer.y;
		const pointerRatioX = focusX / Math.max(layout.horizontalHalfExtent, 1);
		const pointerRatioY = focusY / Math.max(layout.verticalHalfExtent, 1);

		floatObjects.forEach((entry) => {
			const deltaToFocusX = focusX - entry.baseX;
			const deltaToFocusY = focusY - entry.baseY;
			const normalizedX =
				deltaToFocusX / Math.max(layout.horizontalHalfExtent * 0.24, 1);
			const normalizedY =
				deltaToFocusY / Math.max(layout.verticalHalfExtent * 0.28, 1);
			const distance = Math.hypot(normalizedX, normalizedY);
			const proximity = Math.exp(-(distance ** 2) * 5.4);
			const retreat = (1 - proximity) * 150 * strength;
			const focusPull = proximity * 420 * strength;
			const orbitDrift = 1 - proximity * 0.86;

			entry.targetX =
				entry.baseX +
				deltaToFocusX * proximity * 0.24 -
				pointerRatioX * 16 * orbitDrift;
			entry.targetY =
				entry.baseY +
				deltaToFocusY * proximity * 0.24 +
				pointerRatioY * 12 * orbitDrift;
			entry.targetZ = entry.baseZ + focusPull - retreat;
			entry.targetScale = entry.baseScale * (1 + proximity * 0.42);
		});
	};

	const animate = () => {
		pointer.x += (pointerTarget.x - pointer.x) * 0.08;
		pointer.y += (pointerTarget.y - pointer.y) * 0.08;
		camera.lookAt(0, 0, 0);

		updateTargets();

		floatObjects.forEach((entry) => {
			entry.object.position.x +=
				(entry.targetX - entry.object.position.x) * 0.12;
			entry.object.position.y +=
				(entry.targetY - entry.object.position.y) * 0.12;
			entry.object.position.z +=
				(entry.targetZ - entry.object.position.z) * 0.14;

			const nextScale =
				entry.object.scale.x +
				(entry.targetScale - entry.object.scale.x) * 0.12;
			entry.object.scale.set(nextScale, nextScale, nextScale);
		});

		renderScene();
		frameId = window.requestAnimationFrame(animate);
	};

	const handlePointerMove = (event) => {
		const sceneRect = sceneNode.getBoundingClientRect();
		const hitPadding = 18;
		let isNearChip = false;

		for (const entry of floatObjects) {
			const itemRect = entry.item.getBoundingClientRect();
			const nearestX = Math.max(
				itemRect.left - hitPadding,
				Math.min(event.clientX, itemRect.right + hitPadding),
			);
			const nearestY = Math.max(
				itemRect.top - hitPadding,
				Math.min(event.clientY, itemRect.bottom + hitPadding),
			);
			const deltaX = event.clientX - nearestX;
			const deltaY = event.clientY - nearestY;

			if (Math.hypot(deltaX, deltaY) <= hitPadding) {
				isNearChip = true;
				break;
			}
		}

		if (!isNearChip) {
			pointerTarget.x = 0;
			pointerTarget.y = 0;
			return;
		}

		const normalizedX = clamp(
			(event.clientX - sceneRect.left) / sceneRect.width,
			0,
			1,
		);
		const normalizedY = clamp(
			(event.clientY - sceneRect.top) / sceneRect.height,
			0,
			1,
		);

		pointerTarget.x = (normalizedX - 0.5) * layout.horizontalHalfExtent * 2;
		pointerTarget.y = (0.5 - normalizedY) * layout.verticalHalfExtent * 2;
	};

	const handlePointerLeave = () => {
		pointerTarget.x = 0;
		pointerTarget.y = 0;
	};

	updateLayout();
	frameId = window.requestAnimationFrame(animate);

	const resizeObserver =
		typeof ResizeObserver === "undefined"
			? null
			: new ResizeObserver(() => {
					updateLayout();
				});

	resizeObserver?.observe(sceneNode);
	sceneNode.addEventListener("pointermove", handlePointerMove);
	sceneNode.addEventListener("pointerleave", handlePointerLeave);

	node.addEventListener(
		"topic-cloud:destroy",
		() => {
			if (frameId) {
				window.cancelAnimationFrame(frameId);
			}

			resizeObserver?.disconnect();
			sceneNode.removeEventListener("pointermove", handlePointerMove);
			sceneNode.removeEventListener("pointerleave", handlePointerLeave);
		},
		{ once: true },
	);
}
